export const timeAgo = (date: string) => {
  const seconds = Math.floor((new Date().getTime() - new Date(date).getTime()) / 1000);
  let interval = seconds / 31536000;
  if (interval > 1) {
    const years = Math.floor(interval);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    const months = Math.floor(interval);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  interval = seconds / 86400;
  if (interval > 1) {
    const days = Math.floor(interval);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  interval = seconds / 3600;
  if (interval > 1) {
    const hours = Math.floor(interval);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  interval = seconds / 60;
  if (interval > 1) {
    const minutes = Math.floor(interval);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  return 'now';
};

export const formatNumber = (num: number, decimalPoints = 0) => {
  return num.toFixed(decimalPoints).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
};

export const formatPhone = (phoneUnclean: string | number | undefined | null) => {
  if (!phoneUnclean) {
    return '';
  }

  let phone = phoneUnclean.toString();

  if (typeof phoneUnclean === 'number' && phone.length < 10) {
    return phone;
  }

  phone = phone
    .replace('+1', '') // remove +1 from phone number
    .replace(/\D/g, ''); // remove non-numeric characters

  // only use first 10 digits
  phone = phone?.slice(0, 10);
  return phone?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
};

// Strips formatting from a phone number. Returns +1XXXXXXXXXX (E.164) for US numbers;
export const sanitizePhone = (phone?: string | null): string => {
  if (!phone) {
    return '';
  }

  const digits = phone.replace(/\D/g, '');
  if (/^1\d{10}$/.test(digits)) {
    return `+${digits}`;
  }

  if (/^\d{10}$/.test(digits)) {
    return `+1${digits}`;
  }

  return digits;
};

export const formatPhoneHidden = (phone?: string | null) => {
  if (!phone) {
    return;
  }

  return phone
    .replace('+1', '') // remove +1 from phone number
    .replace(/\D/g, '') // remove non-numeric characters
    .replace(/\d(?=\d{4})/g, '*'); // format with * except for last four digits
};

export const formatEmailHidden = (email?: string | null) => {
  if (!email) return;
  const [user, domain] = email.split('@');
  const hiddenUser = user.substring(0, 2) + user.substring(2).replace(/./g, '*');
  return `${hiddenUser}@${domain}`;
};

type Nullable<T> = { [K in keyof T]: T[K] | null | undefined };

export const stripNulls = <T extends Record<string, any>>(obj: Nullable<T>): T => {
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, value]) => value != null)
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return [key, value.map((item) => (item && typeof item === 'object' ? stripNulls(item) : item))];
        }
        if (typeof value === 'object') {
          return [key, stripNulls(value)];
        }
        return [key, value];
      })
  ) as T;
};
